// ⛔ O SIMULADOR ÚNICO — fixa + móvel (Douglas 27/09/2026, mockup aprovado).
//
// A conta fixa (internet, TV, fixo) e a conta móvel chegam separadas, mas a
// simulação é uma só: o catálogo tem dois preços para quase tudo, "no Multi"
// (fibra e móvel juntos) e "Single". Tirar a fibra encarece as linhas; ficar com
// as duas barateia. Estas funções decidem qual versão de cada plano vale.

export type ServicoFixa = 'INTERNET' | 'TV' | 'FIXO' | 'CELULAR' | 'ADICIONAL' | 'OUTRO';

export type ItemContaFixa = {
  servico: ServicoFixa;
  plano: string | null;
  valor: number;
  valor_cheio: number | null;
  desconto: number | null;
  parcela_atual: number | null;
  parcelas_total: number | null;
  fidelidade_ate: string | null;
  itens?: Array<{ descricao?: string; valor?: number }>;
};

/** Serviços que entram na comparação. Celular vai para a simulação móvel; multa e juros não são plano. */
export const SERVICOS_SIMULADOS: ServicoFixa[] = ['INTERNET', 'TV', 'FIXO'];

export const ROTULO_SERVICO: Record<ServicoFixa, string> = {
  INTERNET: 'Internet',
  TV: 'TV',
  FIXO: 'Fixo',
  CELULAR: 'Celular',
  ADICIONAL: 'Adicionais',
  OUTRO: 'Outros',
};

/** Meses até o desconto temporário acabar (parcela 4 de 6 → 2). */
export function mesesAteFimDoDesconto(item: ItemContaFixa): number | null {
  if (!item.desconto || !item.parcela_atual || !item.parcelas_total) return null;
  const faltam = item.parcelas_total - item.parcela_atual;
  return faltam >= 0 ? faltam : null;
}

/** O que o cliente vai pagar quando o desconto acabar. */
export function valorDepoisDoDesconto(item: ItemContaFixa): number {
  if (mesesAteFimDoDesconto(item) == null) return item.valor;
  return Number(item.valor_cheio ?? item.valor + Number(item.desconto || 0));
}

// ── Multi × Single ─────────────────────────────────────────────────────────
// Os nomes do catálogo terminam na condição de preço:
//   "· no Multi com 5G", "· no Multi 5G e Fibra", "· no Multi 5G ou Fibra",
//   "· no Multi", "· no combo Fibra + Celular", "· Single". A base é o nome
//   sem esse último pedaço.

export type ModoPreco = 'COMBO' | 'SO_UM' | 'SINGLE';

const CONDICAO = /\s*·\s*(no Multi[^·]*|no combo[^·]*|Single)\s*$/i;

export function baseDoPlano(nome: string): string {
  return nome.replace(CONDICAO, '').trim();
}

function condicaoDoPlano(nome: string): string {
  const m = nome.match(CONDICAO);
  return (m?.[1] || '').toLowerCase();
}

/** Quão bem a condição do plano serve ao modo: menor é melhor; null = não serve. */
function encaixe(condicao: string, modo: ModoPreco): number | null {
  const e = condicao.includes('5g e fibra') || condicao.startsWith('no combo');
  const ou = condicao.includes('5g ou fibra');
  const multi = condicao.startsWith('no multi');
  const single = condicao === 'single';
  if (modo === 'COMBO') {
    if (e) return 0;
    if (multi && !ou) return 1;
    if (ou) return 2;
    if (single) return 3;
    return 4;
  }
  if (modo === 'SO_UM') {
    if (ou) return 0;
    if (single) return 1;
    return null;
  }
  return single ? 0 : null;
}

type PlanoComNome = { id: string; nome: string; valor?: number | string | null };

/**
 * O plano equivalente no modo de preço pedido (mesma base, outra condição).
 * Sem equivalente, devolve o próprio plano — o simulador avisa que ele não
 * tem versão para aquele modo.
 */
export function equivalenteNoModo<T extends PlanoComNome>(plano: T, modo: ModoPreco, catalogo: T[]): T {
  const base = baseDoPlano(plano.nome);
  const candidatos = catalogo
    .filter((p) => baseDoPlano(p.nome) === base)
    .map((p) => ({ p, nota: encaixe(condicaoDoPlano(p.nome), modo) }))
    .filter((c): c is { p: T; nota: number } => c.nota != null)
    .sort((a, b) => a.nota - b.nota || Number(a.p.valor || 0) - Number(b.p.valor || 0));
  return candidatos[0]?.p ?? plano;
}

/** O plano serve ao modo? (para avisar quando não existe a versão certa) */
export function planoServeAoModo(nome: string, modo: ModoPreco): boolean {
  const nota = encaixe(condicaoDoPlano(nome), modo);
  if (modo === 'COMBO') return nota != null && nota <= 2;
  return nota != null;
}

/**
 * Fibra e móvel juntos = COMBO. Só um dos dois = SO_UM para a TV (que tem a
 * condição "5G ou Fibra") e SINGLE para o que sobrou sozinho.
 */
export function modoDoCliente(temFibra: boolean, temMovel: boolean): ModoPreco {
  if (temFibra && temMovel) return 'COMBO';
  if (temFibra || temMovel) return 'SO_UM';
  return 'SINGLE';
}

/** Parecido o bastante para sugerir: mesma velocidade de internet ou mesmo tipo de TV. */
export function sugerirPlanoFixa<T extends PlanoComNome>(item: ItemContaFixa, catalogo: T[], modo: ModoPreco): T | null {
  const texto = (item.plano || '').toLowerCase();
  const servem = catalogo.filter((p) => planoServeAoModo(p.nome, modo));
  const opcoes = servem.length > 0 ? servem : catalogo;
  const ordenar = (lista: T[]) => [...lista].sort((a, b) => Number(a.valor || 0) - Number(b.valor || 0));
  if (item.servico === 'INTERNET') {
    const vel = texto.match(/(\d+)\s*(mega|giga)/);
    if (vel) {
      const alvo = `${vel[1]} ${vel[2]}`;
      const iguais = opcoes.filter((p) => p.nome.toLowerCase().includes(alvo));
      if (iguais.length > 0) return ordenar(iguais)[0];
    }
  }
  if (item.servico === 'TV') {
    const box = opcoes.filter((p) => /box \(streaming\)/i.test(p.nome));
    if (box.length > 0) return ordenar(box)[0];
  }
  if (item.servico === 'FIXO') {
    const fixo = opcoes.filter((p) => /fixo ilimitado brasil/i.test(p.nome));
    if (fixo.length > 0) return ordenar(fixo)[0];
  }
  return ordenar(opcoes)[0] ?? null;
}
